/**
 * System states — A7 of /docs/design.md.
 *
 * One markup and one copy convention for empty, error, inline loading and
 * "not calculable", so no screen invents its own.
 */
import { AlertTriangle, Inbox, Loader2, MinusCircle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

export function EmptyState({
  icon: Icon = Inbox, title, description, action, className = "", testId = "empty-state",
}) {
  return (
    <div
      data-testid={testId}
      className={`flex flex-col items-center justify-center text-center gap-3 px-6 py-10 ${className}`}
    >
      <span className="flex items-center justify-center w-11 h-11 rounded-full bg-surface-subtle border border-line-soft">
        <Icon className="w-5 h-5 text-content-muted" aria-hidden="true" />
      </span>
      <p className="text-base font-medium text-content">{title}</p>
      {description && (
        <p className="text-sm text-content-muted max-w-sm">{description}</p>
      )}
      {action}
    </div>
  );
}

export function ErrorState({
  title = "Qualcosa non ha funzionato",
  description,
  detail,
  onRetry,
  retryLabel = "Riprova",
  className = "",
  testId = "error-state",
}) {
  return (
    <div
      role="alert"
      data-testid={testId}
      className={`flex flex-col items-center justify-center text-center gap-3 px-6 py-10 ${className}`}
    >
      <span className="flex items-center justify-center w-11 h-11 rounded-full bg-danger-surface border border-danger-border">
        <AlertTriangle className="w-5 h-5 text-danger" aria-hidden="true" />
      </span>
      <p className="text-base font-medium text-content">{title}</p>
      {description && <p className="text-sm text-content-muted max-w-sm">{description}</p>}
      {detail && (
        <details className="text-left max-w-lg w-full">
          <summary className="text-sm text-content-muted cursor-pointer hover:text-content">
            Dettaglio tecnico
          </summary>
          <pre className="mt-2 p-3 rounded-md bg-surface-sunken border border-line-soft text-code text-content-secondary whitespace-pre-wrap break-words">
            {detail}
          </pre>
        </details>
      )}
      {onRetry && (
        <Button data-testid={`${testId}-retry`} variant="outline" onClick={onRetry}>
          <RotateCcw className="w-4 h-4" aria-hidden="true" />
          {retryLabel}
        </Button>
      )}
    </div>
  );
}

/** Inline loading for a control or a small region. */
export function InlineLoading({ label = "Caricamento…", className = "", testId = "inline-loading" }) {
  return (
    <span
      data-testid={testId}
      role="status"
      aria-live="polite"
      className={`inline-flex items-center gap-2 text-sm text-content-muted ${className}`}
    >
      <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
      {label}
    </span>
  );
}

/**
 * A7 — a value that cannot be computed is never rendered as 0 or a bare dash:
 * it says so, and it says why.
 */
export function NotCalculable({ reason, compact = false, className = "", testId = "not-calculable" }) {
  return (
    <span
      data-testid={testId}
      title={reason}
      className={`inline-flex items-center gap-1 text-sm text-content-muted italic ${className}`}
    >
      {!compact && <MinusCircle className="w-3.5 h-3.5" aria-hidden="true" />}
      {compact ? "n/d" : "non calcolabile"}
      {reason && <span className="sr-only">: {reason}</span>}
    </span>
  );
}
